import { useEffect, useState } from 'react';
import type { EditorBlock } from '@/store/editor/types/blocks';
import type { EditorScreen } from '@/store/editor/types/screen';
import type { StylePack } from '@/themes/stylePack.types';
import { findScreenBlockers } from './requiredGate';

/**
 * Состояние шапки квиза по настройкам повтора эталона Slimkit (spec
 * shared/header.md «Что добавить у нас» п.5–8): когда видна кнопка «вперёд»,
 * когда шапка и лого прячутся. Вынесено из QuizScreenWizard.tsx (большой
 * файл) — визард только зовёт эти функции.
 *
 * Все признаки выключены, пока пак/экран не задал новое поле: старые воронки
 * не меняются.
 */

/** Задержка появления «вперёд» после того, как ответ стал валидным, мс (эталон `toNextStepButton/index.tsx:67-82`). */
export const FORWARD_REVEAL_MS = 300;

/**
 * На экране уже есть ответ: хотя бы у одного видимого блока непустое
 * значение, и обязательные заполнены. Экран без полей ответа — false
 * (эталон показывает «вперёд» только на шагах с ответом).
 */
export function screenHasAnswer(
  blocks: EditorBlock[],
  values: Record<string, string>,
  hidden: ReadonlySet<string>,
): boolean {
  const answered = blocks.some((b) => {
    if (hidden.has(b.id)) return false;
    const v = values[b.id];
    return typeof v === 'string' && v.trim().length > 0;
  });
  if (!answered) return false;
  return findScreenBlockers(blocks, values, hidden).length === 0;
}

/**
 * Видимость «вперёд»: появляется через FORWARD_REVEAL_MS после того, как
 * ответ стал валидным; пропадает сразу (ответ снят, экран сменился).
 */
export function useForwardVisible(
  enabled: boolean,
  screenId: string,
  ready: boolean,
): boolean {
  const [visibleFor, setVisibleFor] = useState<string | null>(null);
  useEffect(() => {
    if (!enabled || !ready) {
      setVisibleFor(null);
      return undefined;
    }
    const timer = setTimeout(() => setVisibleFor(screenId), FORWARD_REVEAL_MS);
    return () => clearTimeout(timer);
  }, [enabled, ready, screenId]);
  return enabled && ready && visibleFor === screenId;
}

/** Порог «клавиатура открыта»: видимая высота меньше окна хотя бы на столько, px. */
export const KEYBOARD_MIN_PX = 120;

const TOUCH_UA = /iPhone|iPad|iPod|Android/i;

function isTextField(el: Element | null): boolean {
  if (!el) return false;
  const tag = el.tagName;
  if (tag === 'TEXTAREA') return true;
  if (tag !== 'INPUT') return false;
  const type = (el as HTMLInputElement).type;
  return !['checkbox', 'radio', 'button', 'submit', 'range', 'file'].includes(
    type,
  );
}

/** Чистая проверка (для теста): телефон, фокус в поле и видимая область ужалась. */
export function isKeyboardOpen(input: {
  userAgent: string;
  active: Element | null;
  innerHeight: number;
  viewportHeight: number | undefined;
}): boolean {
  if (!TOUCH_UA.test(input.userAgent)) return false;
  if (!isTextField(input.active)) return false;
  if (typeof input.viewportHeight !== 'number') return false;
  return input.innerHeight - input.viewportHeight >= KEYBOARD_MIN_PX;
}

/**
 * Открыта ли экранная клавиатура телефона (эталон `stepsContent/index.tsx:114-138`
 * — только телефоны по userAgent). Работает по `visualViewport`; нет его —
 * всегда false. Выключено (`enabled: false`) — слушателей нет вовсе.
 */
export function useVirtualKeyboardOpen(enabled: boolean): boolean {
  const [open, setOpen] = useState(false);
  useEffect(() => {
    if (!enabled || typeof window === 'undefined') {
      setOpen(false);
      return undefined;
    }
    const vv = window.visualViewport;
    const update = (): void =>
      setOpen(
        isKeyboardOpen({
          userAgent: navigator.userAgent,
          active: document.activeElement,
          innerHeight: window.innerHeight,
          viewportHeight: vv?.height,
        }),
      );
    vv?.addEventListener('resize', update);
    document.addEventListener('focusin', update);
    document.addEventListener('focusout', update);
    return () => {
      vv?.removeEventListener('resize', update);
      document.removeEventListener('focusin', update);
      document.removeEventListener('focusout', update);
    };
  }, [enabled]);
  return enabled && open;
}

export interface ChromeVisibility {
  /** Шапку не рисовать вовсе (без распорки) — поле экрана или клавиатура. */
  hideChrome: boolean;
  /** Лого не рисовать (ни в потоке, ни на десктопе). */
  hideLogo: boolean;
}

export function resolveChromeVisibility(
  pack: StylePack | undefined,
  screen: Pick<EditorScreen, 'hideChrome' | 'hideLogo'> | undefined,
  keyboardOpen: boolean,
): ChromeVisibility {
  const keyboardHides = keyboardOpen && pack?.chrome.hideOnKeyboard === true;
  return {
    hideChrome: screen?.hideChrome === true || keyboardHides,
    hideLogo: screen?.hideLogo === true || keyboardHides,
  };
}
